import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Users,
  Filter,
  X,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Calendar,
  DollarSign,
  CreditCard,
  CheckCircle,
  XCircle,
  Clock,
  Tag,
  FileText,
  RefreshCw,
  Eye,
  ShoppingCart,
  TrendingUp,
  AlertCircle,
  Check,
  BookOpen,
  SlidersHorizontal,
  RotateCcw
} from 'lucide-react';
import useStudentOrderManagement from '../../../../hooks/api/useStudentOrderManagement';

const StudentOrderManagement = () => {
  const {
    loading,
    error,
    orders,
    selectedOrder,
    summary,
    pagination,
    loadingOrderDetails,
    getAllOrders,
    getOrderById,
    searchOrders,
    filterOrders,
    getPaymentStatusesDropdown,
    clearError
  } = useStudentOrderManagement();

  // Component state
  const [filters, setFilters] = useState({
    orderNo: '',
    customerName: '',
    statusId: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [showSummary, setShowSummary] = useState(true); // Collapsible summary section
  const [showOrderDetails, setShowOrderDetails] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [paymentStatuses, setPaymentStatuses] = useState([]);
  const [loadingDropdowns, setLoadingDropdowns] = useState(false);

  // Build active-filter metadata (count + chips)
  const { activeFilterCount, activeFilterChips } = useMemo(() => {
    const labels = {
      orderNo: 'Order No',
      customerName: 'Customer Name',
      statusId: 'Status'
    };

    const isActive = (k, v) => {
      if (typeof v === 'boolean') return v === true;
      if (Array.isArray(v)) return v.length > 0;
      return v !== '' && v !== null && v !== undefined;
    };

    const displayValue = (k, v) => {
      switch (k) {
        case 'statusId':
          const status = paymentStatuses.find(s => s.id.toString() === v);
          return status ? status.name : v;
        default:
          return String(v);
      }
    };

    const chips = Object.entries(filters)
      .filter(([k, v]) => isActive(k, v))
      .map(([k, v]) => ({ key: k, label: labels[k] || k, value: displayValue(k, v) }));

    return { activeFilterCount: chips.length, activeFilterChips: chips };
  }, [filters, paymentStatuses]);

  // Check if any filters are active
  const hasActiveFilters = useCallback(() => {
    return filters.orderNo || filters.customerName || filters.statusId;
  }, [filters]);

  // Load dropdown data
  const loadDropdownData = useCallback(async () => {
    setLoadingDropdowns(true);
    try {
      const statusesData = await getPaymentStatusesDropdown();
      setPaymentStatuses(statusesData || []);
    } catch (err) {
      console.error('Failed to load dropdown data:', err);
    } finally {
      setLoadingDropdowns(false);
    }
  }, [getPaymentStatusesDropdown]);

  // Load orders function - uses filterOrders when filters are active
  const loadOrders = useCallback(async (page = 1) => {
    try {
      if (hasActiveFilters()) {
        await filterOrders(filters, page, pagination.pageSize);
      } else {
        await getAllOrders(page, pagination.pageSize);
      }
    } catch (err) {
      console.error('Failed to load orders:', err);
    }
  }, [getAllOrders, filterOrders, filters, hasActiveFilters, pagination.pageSize]);

  // Load orders and dropdowns on component mount
  useEffect(() => {
    loadOrders();
    loadDropdownData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle filter
  const handleFilter = useCallback(async () => {
    try {
      await filterOrders(filters, 1, pagination.pageSize);
    } catch (err) {
      console.error('Failed to filter orders:', err);
    }
  }, [filters, filterOrders, pagination.pageSize]);

  // Clear filters
  const clearFilters = useCallback(async () => {
    setFilters({
      orderNo: '',
      customerName: '',
      statusId: ''
    });
    await getAllOrders(1, pagination.pageSize);
  }, [getAllOrders, pagination.pageSize, setFilters]);

  // Reset a single filter and re-fetch
  const removeFilter = useCallback(async (key) => {
    const resetValue = typeof filters[key] === 'boolean'
      ? false
      : Array.isArray(filters[key])
        ? []
        : '';
    const next = { ...filters, [key]: resetValue };
    setFilters(next);
    try {
      // Check if any filters remain active after removal
      const remainingActive = Object.entries(next).some(([k, v]) => {
        if (k === key) return false;
        return v && v !== '' && v !== false && v.length !== 0;
      });
      if (remainingActive) {
        await filterOrders(next, 1, pagination.pageSize);
      } else {
        await getAllOrders(1, pagination.pageSize);
      }
    } catch (err) {
      console.error('Failed to remove filter:', err);
    }
  }, [filters, filterOrders, getAllOrders, pagination.pageSize]);

  // Handle page change
  const handlePageChange = useCallback((newPage) => {
    loadOrders(newPage);
  }, [loadOrders]);

  // Handle refresh
  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    try {
      if (hasActiveFilters()) {
        await filterOrders(filters, pagination.currentPage, pagination.pageSize);
      } else {
        await getAllOrders(pagination.currentPage, pagination.pageSize);
      }
    } catch (err) {
      console.error('Failed to refresh orders:', err);
    } finally {
      setIsRefreshing(false);
    }
  }, [getAllOrders, filterOrders, filters, hasActiveFilters, pagination.currentPage, pagination.pageSize]);

  // Handle view order details
  const handleViewOrder = useCallback(async (orderId) => {
    try {
      await getOrderById(orderId);
      setShowOrderDetails(true);
    } catch (err) {
      console.error('Failed to fetch order details:', err);
    }
  }, [getOrderById]);

  // Format date
  const formatDate = useCallback((dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }, []);

  // Format currency
  const formatCurrency = useCallback((amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount || 0);
  }, []);

  // Get status color
  const getStatusColor = useCallback((statusName) => {
    switch (statusName?.toLowerCase()) {
      case 'paid':
        return 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400';
      case 'pending':
        return 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400';
      case 'cancelled':
        return 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400';
      default:
        return 'bg-gray-100 dark:bg-gray-900/30 text-gray-700 dark:text-gray-400';
    }
  }, []);

  // Get status icon
  const getStatusIcon = useCallback((statusName) => {
    switch (statusName?.toLowerCase()) {
      case 'paid':
        return <CheckCircle className="w-4 h-4" />;
      case 'pending':
        return <Clock className="w-4 h-4" />;
      case 'cancelled':
        return <XCircle className="w-4 h-4" />;
      default:
        return <AlertCircle className="w-4 h-4" />;
    }
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 p-3 lg:p-4">
      {/* Search and Filters - Modern Compact Design */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-visible">
        {/* Filter Header Bar */}
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-gray-50 to-white dark:from-gray-800 dark:to-gray-800 rounded-t-xl">
          <div className="flex flex-col lg:flex-row lg:items-center gap-3">
            {/* Left: Title & Stats */}
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <div className="flex items-center gap-2.5">
                <div className="p-1.5 bg-gradient-to-r from-green-500 to-emerald-600 rounded-lg shadow-md">
                  <ShoppingCart className="w-4 h-4 text-white" />
                </div>
                <div className="flex flex-col">
                  <h1 className="text-lg font-semibold text-gray-900 dark:text-white leading-tight">
                    Student Order Management
                  </h1>
                  <p className="text-xs text-gray-500 dark:text-gray-400 leading-tight">
                    {pagination.totalCount} orders
                  </p>
                </div>
              </div>
              {activeFilterCount > 0 && (
                <span className="hidden sm:inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300">
                  {activeFilterCount} filter{activeFilterCount === 1 ? '' : 's'}
                </span>
              )}
            </div>

            {/* Right: Filter Actions */}
            <div className="flex items-center gap-2">
              <button
                onClick={handleRefresh}
                disabled={isRefreshing}
                className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Refresh data"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${isRefreshing ? 'animate-spin' : ''}`} />
              </button>
              <div className="px-2.5 py-1 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-md border border-blue-200 dark:border-blue-800">
                <div className="text-[10px] text-gray-600 dark:text-gray-400 font-medium uppercase tracking-wide">Total</div>
                <div className="text-sm font-bold text-gray-900 dark:text-white leading-none">{pagination.totalCount}</div>
              </div>
              {activeFilterCount > 0 && (
                <button
                  onClick={clearFilters}
                  className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
                >
                  <RotateCcw className="w-4 h-4" />
                  <span className="hidden sm:inline">Reset</span>
                </button>
              )}
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                  showFilters
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
                aria-expanded={showFilters}
                aria-controls="filter-panel"
              >
                <SlidersHorizontal className="w-4 h-4" />
                <span className="hidden sm:inline">Filters</span>
                <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${showFilters ? 'rotate-180' : ''}`} />
              </button>
              <button
                onClick={handleFilter}
                className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 rounded-lg shadow-sm hover:shadow transition-all"
              >
                <Filter className="w-4 h-4" />
                <span className="hidden sm:inline">Apply</span>
              </button>
            </div>
          </div>
        </div>

        {/* Expandable Filter Panel */}
        <div
          id="filter-panel"
          className={`transition-all duration-300 ease-in-out ${
            showFilters ? 'max-h-[500px] opacity-100 overflow-visible' : 'max-h-0 opacity-0 overflow-hidden'
          }`}
        >
          <div className="p-4 space-y-4 bg-gray-50/60 dark:bg-gray-900/20 rounded-b-2xl">
            {/* Text Inputs Group */}
            <div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
                <div className="relative">
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Order Number</label>
                  <div className="relative">
                    <Tag className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search by order no..."
                      value={filters.orderNo}
                      onChange={(e) => setFilters({ ...filters, orderNo: e.target.value })}
                      className="w-full pl-9 pr-3 py-2 text-sm bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:text-white transition-all shadow-sm"
                    />
                  </div>
                </div>
                <div className="relative">
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Customer Name</label>
                  <div className="relative">
                    <Users className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search by name..."
                      value={filters.customerName}
                      onChange={(e) => setFilters({ ...filters, customerName: e.target.value })}
                      className="w-full pl-9 pr-3 py-2 text-sm bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:text-white transition-all shadow-sm"
                    />
                  </div>
                </div>
             
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Payment Status</label>
                  <div className="relative">
                    <CheckCircle className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <select
                      value={filters.statusId}
                      onChange={(e) => setFilters({ ...filters, statusId: e.target.value })}
                      disabled={loadingDropdowns}
                      className="w-full pl-9 pr-8 py-2 text-sm bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:text-white transition-all appearance-none cursor-pointer shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <option value="">All Status</option>
                      {paymentStatuses.map((status) => (
                        <option key={status.id} value={status.id.toString()}>
                          {status.name}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                    {loadingDropdowns && (
                      <div className="absolute right-8 top-1/2 -translate-y-1/2">
                        <div className="w-3 h-3 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin"></div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Summary Panel - Collapsible */}
        <div className="bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
          {/* Summary Header Toggle */}
          <button
            onClick={() => setShowSummary(!showSummary)}
            className="w-full px-4 py-3 flex items-center justify-between bg-gray-50/80 dark:bg-gray-800/80 hover:bg-gray-100 dark:hover:bg-gray-700/80 transition-colors"
          >
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Order Summary</span>
              {summary && (
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  ({summary.totalOrders || 0} total)
                </span>
              )}
            </div>
            <ChevronDown className={`w-4 h-4 text-gray-500 dark:text-gray-400 transition-transform duration-200 ${showSummary ? 'rotate-180' : ''}`} />
          </button>

          {/* Summary Cards */}
          <div
            className={`transition-all duration-300 ease-in-out ${
              showSummary ? 'max-h-[300px] opacity-100 overflow-visible' : 'max-h-0 opacity-0 overflow-hidden'
            }`}
          >
            <div className="p-4 bg-gray-50/60 dark:bg-gray-900/20">
              {summary ? (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                  {/* Total Orders */}
                  <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="p-1.5 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                        <ShoppingCart className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                      </div>
                      <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Total Orders</span>
                    </div>
                    <div className="text-2xl font-bold text-gray-900 dark:text-white">{summary.totalOrders || 0}</div>
                  </div>

                  {/* Paid Orders */}
                  <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="p-1.5 bg-green-100 dark:bg-green-900/30 rounded-lg">
                        <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />
                      </div>
                      <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Paid</span>
                    </div>
                    <div className="text-2xl font-bold text-gray-900 dark:text-white">{summary.paidCount || 0}</div>
                  </div>

                  {/* Pending Orders */}
                  <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="p-1.5 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg">
                        <Clock className="w-4 h-4 text-yellow-600 dark:text-yellow-400" />
                      </div>
                      <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Pending</span>
                    </div>
                    <div className="text-2xl font-bold text-gray-900 dark:text-white">{summary.pendingCount || 0}</div>
                  </div>

                  {/* Cancelled Orders */}
                  <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="p-1.5 bg-red-100 dark:bg-red-900/30 rounded-lg">
                        <XCircle className="w-4 h-4 text-red-600 dark:text-red-400" />
                      </div>
                      <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Cancelled</span>
                    </div>
                    <div className="text-2xl font-bold text-gray-900 dark:text-white">{summary.cancelledCount || 0}</div>
                  </div>

                  {/* Failed Orders */}
                  <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="p-1.5 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
                        <AlertCircle className="w-4 h-4 text-orange-600 dark:text-orange-400" />
                      </div>
                      <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Failed</span>
                    </div>
                    <div className="text-2xl font-bold text-gray-900 dark:text-white">{summary.failedCount || 0}</div>
                  </div>

                  {/* Free Orders */}
                  <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="p-1.5 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                        <DollarSign className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                      </div>
                      <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Free Orders</span>
                    </div>
                    <div className="text-2xl font-bold text-gray-900 dark:text-white">{summary.freeOrdersCount || 0}</div>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center py-8 text-gray-500 dark:text-gray-400">
                  <div className="flex items-center gap-2">
                    <div className="w-5 h-5 border-2 border-gray-300 dark:border-gray-600 border-t-blue-500 rounded-full animate-spin"></div>
                    <span>Loading summary...</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Active Filter Chips */}
      {activeFilterChips.length > 0 && (
        <div className="mt-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 flex flex-wrap items-center gap-2">
          <span className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mr-1">
            Active Filters:
          </span>
          {activeFilterChips.map((chip) => (
            <span
              key={chip.key}
              className="inline-flex items-center gap-1.5 pl-3 pr-1.5 py-1 rounded-full text-xs font-medium bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800"
            >
              <span className="text-blue-600/70 dark:text-blue-400/70">{chip.label}:</span>
              <span className="font-semibold">{chip.value}</span>
              <button
                onClick={() => removeFilter(chip.key)}
                className="ml-1 p-0.5 rounded-full hover:bg-blue-200 dark:hover:bg-blue-800 transition-colors"
                aria-label={`Remove ${chip.label} filter`}
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
          <button
            onClick={clearFilters}
            className="ml-auto text-xs font-medium text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
          >
            Clear all
          </button>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="bg-gradient-to-r from-red-50 to-red-100 dark:from-red-900/20 dark:to-red-800/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-6 py-4 rounded-xl flex items-center justify-between shadow-lg my-4">
          <div className="flex items-center gap-3">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <span className="font-medium">{error}</span>
          </div>
          <button
            onClick={clearError}
            className="text-red-500 hover:text-red-700 dark:hover:text-red-300 transition-colors duration-200 p-1 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* Orders Table */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 mt-5 overflow-hidden">
        {/* Desktop Table - Scrollable Container */}
        <div className="hidden md:block w-full overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-900">
          <table className="w-full min-w-[900px] table-fixed border-collapse">
            <thead className="sticky top-0 z-10 bg-gray-100 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
              <tr>
                <th className="w-[180px] pl-4 pr-2 py-3 text-left text-[12px] font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-400">
                  Order No
                </th>
                <th className="w-[150px] px-2 py-3 text-left text-[12px] font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-400">
                  Customer
                </th>
                <th className="w-[120px] px-2 py-3 text-left text-[12px] font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-400">
                  Amount
                </th>
                <th className="w-[100px] px-2 py-3 text-left text-[12px] font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-400">
                  Payment Status
                </th>
                <th className="w-[140px] px-2 py-3 text-left text-[12px] font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-400">
                 Order Date
                </th>
                <th className="w-[100px] px-2 py-3 text-left text-[12px] font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-400">
                  Actions
                </th>
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {loading ? (
                <tr>
                  <td colSpan="6" className="px-6 py-14 text-center">
                    <div className="flex flex-col items-center gap-3">
                      <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-500 border-t-transparent"></div>
                      <span className="font-medium text-gray-500 dark:text-gray-400">
                        Loading orders...
                      </span>
                    </div>
                  </td>
                </tr>
              ) : orders.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-14 text-center">
                    <div className="flex flex-col items-center gap-3">
                      <ShoppingCart className="h-12 w-12 text-gray-400" />
                      <span className="text-lg font-medium text-gray-500 dark:text-gray-400">
                        No orders found
                      </span>
                      <span className="text-sm text-gray-400 dark:text-gray-500">
                        Try adjusting your search or filters
                      </span>
                    </div>
                  </td>
                </tr>
              ) : (
                orders.map((order) => (
                  <tr
                    key={order.id}
                    className="transition-colors duration-150 hover:bg-gray-50 dark:hover:bg-gray-800/60"
                  >
                    <td className="pl-4 pr-2 py-3 align-top">
                      <div className="min-w-0 space-y-1.5">
                        <div className="truncate text-sm font-semibold text-gray-900 dark:text-white">
                          {order.orderNo}
                        </div>
                      </div>
                    </td>
                    <td className="px-2 py-3 align-top">
                      <div className="min-w-0 space-y-1.5">
                        <div className="truncate text-sm font-medium text-gray-900 dark:text-white">
                          {order.customerFullName || 'N/A'}
                        </div>
                      </div>
                    </td>
                    <td className="px-2 py-3 align-top">
                      <div className="min-w-0 space-y-1">
                        <div className="text-sm font-semibold text-gray-900 dark:text-white">
                          {formatCurrency(order.totalAmount, order.currencyCode)}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          Sub: {formatCurrency(order.subtotalAmount, order.currencyCode)}
                        </div>
                        {order.discountAmount > 0 && (
                          <div className="text-xs text-green-600 dark:text-green-400">
                            -{formatCurrency(order.discountAmount, order.currencyCode)}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-2 py-3 align-top">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(order.statusName)}`}>
                        {getStatusIcon(order.statusName)}
                        {order.statusName}
                      </span>
                    </td>
                    <td className="px-2 py-3 align-top">
                      <div className="min-w-0 space-y-1">
                        <div className="text-sm text-gray-900 dark:text-white">
                          {formatDate(order.createdAt)}
                        </div>
                        {/* {order.paidAt && (
                          <div className="text-xs text-green-600 dark:text-green-400">
                            Paid: {formatDate(order.paidAt)}
                          </div>
                        )} */}
                      </div>
                    </td>
                    <td className="px-2 py-3 align-top">
                      <button
                        onClick={() => handleViewOrder(order.id)}
                        className="inline-flex items-center gap-1.5 px-2 py-1.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded-lg hover:bg-blue-200 dark:hover:bg-blue-900/50 transition-all duration-200 text-xs font-medium"
                      >
                        <Eye className="w-3.5 h-3.5" />
                        View
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-700">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="text-sm text-gray-700 dark:text-gray-300 font-medium">
                Showing <span className="font-semibold text-gray-900 dark:text-white">{((pagination.currentPage - 1) * pagination.pageSize) + 1}</span> to{' '}
                <span className="font-semibold text-gray-900 dark:text-white">{Math.min(pagination.currentPage * pagination.pageSize, pagination.totalCount)}</span> of{' '}
                <span className="font-semibold text-gray-900 dark:text-white">{pagination.totalCount}</span> results
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handlePageChange(pagination.currentPage - 1)}
                  disabled={!pagination.hasPreviousPage}
                  className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-500 transition-all duration-200 shadow-sm hover:shadow-md"
                >
                  <ChevronLeft className="w-4 h-4" />
                  <span>Previous</span>
                </button>
                
                <div className="flex items-center gap-1">
                  {/* Page numbers */}
                  {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                    let pageNum;
                    if (pagination.totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (pagination.currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (pagination.currentPage >= pagination.totalPages - 2) {
                      pageNum = pagination.totalPages - 4 + i;
                    } else {
                      pageNum = pagination.currentPage - 2 + i;
                    }
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`w-10 h-10 rounded-lg font-medium transition-all duration-200 ${
                          pageNum === pagination.currentPage
                            ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg'
                            : 'bg-white dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-500'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                
                <button
                  onClick={() => handlePageChange(pagination.currentPage + 1)}
                  disabled={!pagination.hasNextPage}
                  className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-500 transition-all duration-200 shadow-sm hover:shadow-md"
                >
                  <span>Next</span>
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Order Details Modal */}
      {showOrderDetails && selectedOrder && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center p-4 z-50 animate-fadeIn">
          <div className="bg-gradient-to-br from-white to-gray-50 dark:from-gray-800 dark:to-gray-900 rounded-3xl max-w-5xl w-full max-h-[85vh] overflow-y-auto shadow-2xl border border-gray-200/50 dark:border-gray-700/50 animate-slideUp">
            {/* Modal Header */}
            <div className="sticky top-0 bg-gradient-to-r from-green-600 to-emerald-600 dark:from-green-700 dark:to-emerald-700 p-6 rounded-t-3xl border-b border-gray-200/50 dark:border-gray-700/50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-white/20 backdrop-blur-sm rounded-2xl">
                    <ShoppingCart className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-white">Order Details</h2>
                    <p className="text-green-100 text-sm">{selectedOrder.orderNo}</p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowOrderDetails(false);
                  }}
                  className="p-3 bg-white/20 backdrop-blur-sm rounded-2xl hover:bg-white/30 transition-all duration-200 group"
                >
                  <X className="w-5 h-5 text-white group-hover:rotate-90 transition-transform duration-200" />
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-6 lg:p-8">
              {/* Order Overview */}
              <div className="bg-gradient-to-r from-green-50 via-emerald-50 to-teal-50 dark:from-green-900/20 dark:via-emerald-900/20 dark:to-teal-900/20 rounded-2xl p-6 mb-8 border border-green-200/50 dark:border-green-800/30">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Order Information</h3>
                    <div className="space-y-3">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-green-500 rounded-xl">
                          <Tag className="w-4 h-4 text-white" />
                        </div>
                        <div>
                          <p className="text-sm text-gray-600 dark:text-gray-400">Order Number</p>
                          <p className="font-semibold text-gray-900 dark:text-white">{selectedOrder.orderNo}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-blue-500 rounded-xl">
                          <Users className="w-4 h-4 text-white" />
                        </div>
                        <div>
                          <p className="text-sm text-gray-600 dark:text-gray-400">Customer</p>
                          <p className="font-semibold text-gray-900 dark:text-white">
                            {selectedOrder.customerFullName || 'N/A'} 
                          </p>
                        </div>
                      </div>
                      
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Payment Information</h3>
                    <div className="space-y-3">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-yellow-500 rounded-xl">
                          <DollarSign className="w-4 h-4 text-white" />
                        </div>
                        <div>
                          <p className="text-sm text-gray-600 dark:text-gray-400">Total Amount</p>
                          <p className="font-semibold text-gray-900 dark:text-white text-lg">
                            {formatCurrency(selectedOrder.totalAmount, selectedOrder.currencyCode)}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-orange-500 rounded-xl">
                          <Calendar className="w-4 h-4 text-white" />
                        </div>
                        <div>
                          <p className="text-sm text-gray-600 dark:text-gray-400">Order Date</p>
                          <p className="font-semibold text-gray-900 dark:text-white">{formatDate(selectedOrder.createdAt)}</p>
                        </div>
                      </div>
                      {/* {selectedOrder.paidAt && (
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-green-500 rounded-xl">
                            <Check className="w-4 h-4 text-white" />
                          </div>
                          <div>
                            <p className="text-sm text-gray-600 dark:text-gray-400">Paid Date</p>
                            <p className="font-semibold text-gray-900 dark:text-white">{formatDate(selectedOrder.paidAt)}</p>
                          </div>
                        </div>
                      )} */}
                    </div>
                  </div>
                </div>
              </div>

              {/* Financial Details */}
              <div className="bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-blue-900/30 dark:to-indigo-900/30 rounded-2xl p-6 mb-8 border border-blue-200/50 dark:border-blue-800/30">
                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-3">
                  <div className="p-2 bg-blue-500 rounded-xl">
                    <CreditCard className="w-5 h-5 text-white" />
                  </div>
                  Financial Breakdown
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center p-3 bg-white dark:bg-gray-700 rounded-xl">
                    <span className="text-gray-600 dark:text-gray-400">Subtotal</span>
                    <span className="font-semibold text-gray-900 dark:text-white">
                      {formatCurrency(selectedOrder.subtotalAmount, selectedOrder.currencyCode)}
                    </span>
                  </div>
                  {selectedOrder.discountAmount > 0 && (
                    <div className="flex justify-between items-center p-3 bg-white dark:bg-gray-700 rounded-xl">
                      <span className="text-gray-600 dark:text-gray-400">Discount</span>
                      <span className="font-semibold text-green-600 dark:text-green-400">
                        -{formatCurrency(selectedOrder.discountAmount, selectedOrder.currencyCode)}
                      </span>
                    </div>
                  )}
                  {selectedOrder.taxAmount > 0 && (
                    <div className="flex justify-between items-center p-3 bg-white dark:bg-gray-700 rounded-xl">
                      <span className="text-gray-600 dark:text-gray-400">Tax</span>
                      <span className="font-semibold text-gray-900 dark:text-white">
                        {formatCurrency(selectedOrder.taxAmount, selectedOrder.currencyCode)}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between items-center p-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-xl">
                    <span className="font-semibold">Total Amount</span>
                    <span className="font-bold text-lg">
                      {formatCurrency(selectedOrder.totalAmount, selectedOrder.currencyCode)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Order Items */}
              {selectedOrder.orderItems && selectedOrder.orderItems.length > 0 && (
                <div className="bg-gradient-to-br from-indigo-50 to-blue-100 dark:from-indigo-900/30 dark:to-blue-900/30 p-6 rounded-2xl border border-indigo-200/50 dark:border-indigo-800/30">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-indigo-500 rounded-xl">
                      <ShoppingCart className="w-5 h-5 text-white" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white">Order Items ({selectedOrder.orderItems.length})</h3>
                  </div>
                  <div className="space-y-3">
                    {selectedOrder.orderItems.map((item, index) => (
                      <div key={item.id || index} className="bg-white dark:bg-gray-700 p-4 rounded-xl border border-gray-200 dark:border-gray-600">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <div className="p-1 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg">
                                <BookOpen className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                              </div>
                              <span className="text-xs font-medium px-2 py-1 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 rounded-full">
                                Type: {item.resourceType || 'Course'}
                              </span>
                              <span className="text-xs font-medium px-2 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 rounded-full">
                                Package: {item.packageName || 'N/A'}
                              </span>
                              {item.levelName && (
                                <span className="text-xs font-medium px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 rounded-full">
                                  {item.levelName}
                                </span>
                              )}
                            </div>
                            <h4 className="font-semibold text-gray-900 dark:text-white mb-1">
                              {item.resourceTitle || item.titleSnapshot}
                            </h4>
                            {item.careerPathName && (
                              <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                                Career Path: {item.careerPathName}
                              </p>
                            )}
                            <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
                              
                              {item.levelId && <span>Level ID: {item.levelId}</span>}
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-sm text-gray-500 dark:text-gray-400 line-through">
                              {formatCurrency(item.unitPrice, selectedOrder.currencyCode)}
                            </div>
                            <div className="text-lg font-bold text-gray-900 dark:text-white">
                              {formatCurrency(item.finalPrice, selectedOrder.currencyCode)}
                            </div>
                            {item.discount > 0 && (
                              <div className="text-sm text-green-600 dark:text-green-400">
                                Save {formatCurrency(item.discount, selectedOrder.currencyCode)}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Additional Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                {selectedOrder.couponCode && (
                  <div className="bg-gradient-to-br from-purple-50 to-pink-100 dark:from-purple-900/30 dark:to-pink-900/30 p-6 rounded-2xl border border-purple-200/50 dark:border-purple-800/30">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="p-2 bg-purple-500 rounded-xl">
                        <Tag className="w-5 h-5 text-white" />
                      </div>
                      <h4 className="font-bold text-gray-900 dark:text-white">Coupon Code</h4>
                    </div>
                    <p className="text-gray-700 dark:text-gray-300 font-medium text-lg">{selectedOrder.couponCode}</p>
                  </div>
                )}
                {selectedOrder.notes && (
                  <div className="bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-600 p-6 rounded-2xl border border-gray-200/50 dark:border-gray-600/50">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="p-2 bg-gray-600 rounded-xl">
                        <FileText className="w-5 h-5 text-white" />
                      </div>
                      <h4 className="font-bold text-gray-900 dark:text-white">Notes</h4>
                    </div>
                    <p className="text-gray-700 dark:text-gray-300">{selectedOrder.notes}</p>
                  </div>
                )}
              </div>

              {/* Payment Method Information */}
              {(selectedOrder.cardHolderName || selectedOrder.paymentMethodType) && (
                <div className="bg-gradient-to-br from-green-50 to-emerald-100 dark:from-green-900/30 dark:to-emerald-900/30 p-6 rounded-2xl border border-green-200/50 dark:border-green-800/30">
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-3">
                    <div className="p-2 bg-green-500 rounded-xl">
                      <CreditCard className="w-5 h-5 text-white" />
                    </div>
                    Payment Method
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {selectedOrder.cardHolderName && (
                      <div>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Card Holder Name</p>
                        <p className="font-semibold text-gray-900 dark:text-white">{selectedOrder.cardHolderName}</p>
                      </div>
                    )}
                    {selectedOrder.paymentMethodType && (
                      <div>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Method Type</p>
                        <p className="font-semibold text-gray-900 dark:text-white">{selectedOrder.paymentMethodType}</p>
                      </div>
                    )}
                    {selectedOrder.transactionId && (
                      <div>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Transaction ID</p>
                        <p className="font-semibold text-gray-900 dark:text-white">{selectedOrder.transactionId}</p>
                      </div>
                    )}
                    {selectedOrder.paymentStatusName && (
                      <div>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Payment Status</p>
                        <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(selectedOrder.paymentStatusName)}`}>
                          {getStatusIcon(selectedOrder.paymentStatusName)}
                          {selectedOrder.paymentStatusName}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentOrderManagement;
